import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';
import Cookies from 'js-cookie';
import { Loader2, X, Check, Download } from 'lucide-react';

import { DATE_BACKEND_FORMAT, ORDER_STATUSES, TOKEN_NAME } from '../../constants';
import { doGetLocations } from '../../modules/locations/stores/thunk';
import { doGetCompanies } from '../../modules/companies/stores/thunk';
import { selectAllCompanies } from '../../modules/companies/stores/slice';
import { selectAllLocations } from '../../modules/locations/stores/slice';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel } from '../ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DatePickerWithRange } from '../ui/date-picker-range';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { DateRange } from 'react-day-picker';
import { AppDispatch, RootState } from '../../redux/store';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import { formatOrderStatus } from '../Elements/OrderTimeLine';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { getStatusBadgeStyles } from '../../utils/statusStyles';

interface OrdersFilterProps {
  isFetching: boolean;
  query: Record<string, any>;
  onSubmit: (params: Record<string, any>, reset?: boolean) => void;
  setTransactionData: (data: any) => void;
}

interface FormValues {
  name: string;
  orderType: string;
  statuses: string[];
  deliveryDate: DateRange | undefined;
  createdAt: DateRange | undefined;
  label: string;
  companyId: string;
  locationId: string;
  claim: string;
  mailbox: string;
}

interface Company {
  id: number;
  name: string;
}

interface Location {
  id: number;
  name: string;
  companyId: number;
  locationType: string;
}

const OrdersFilter: React.FC<OrdersFilterProps> = ({ isFetching, query, onSubmit, setTransactionData }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [showDownload, setShowDownload] = useState(false);
  const [companySelectId, setCompanySelectId] = useState<string>('');
  const [optionsLocation, setOptionsLocation] = useState<Array<{ label: string; value: string }>>([]);

  const companies = useSelector((state: RootState) => selectAllCompanies(state));
  const locations = useSelector((state: RootState) => selectAllLocations(state));

  const form = useForm<FormValues>({
    defaultValues: {
      name: '',
      orderType: '',
      statuses: [],
      deliveryDate: { from: undefined, to: undefined },
      createdAt: { from: undefined, to: undefined },
      label: '',
      companyId: '',
      locationId: '',
      claim: '',
      mailbox: '',
    },
  });

  useEffect(() => {
    dispatch(doGetLocations({}));
    dispatch(doGetCompanies({ page: 1, take: 10000 }));
  }, [dispatch]);

  useEffect(() => {
    const values = getInitialValues();
    form.reset(values);
  }, [query, form]);

  const getInitialValues = (): FormValues => {
    const deliveryDateFromValue = query?.deliveryDateFrom;
    const deliveryDateToValue = query?.deliveryDateTo;
    const createdAtFromValue = query?.createdAtFrom;
    const createdAtToValue = query?.createdAtTo;

    return {
      name: query?.name || '',
      orderType: query?.orderType || '',
      statuses: query?.statuses
        ? Array.isArray(query?.statuses)
          ? query?.statuses
          : decodeURIComponent(query?.statuses).split(',')
        : [],
      deliveryDate:
        deliveryDateFromValue || deliveryDateToValue
          ? {
              from: deliveryDateFromValue ? new Date(deliveryDateFromValue) : undefined,
              to: deliveryDateToValue ? new Date(deliveryDateToValue) : undefined,
            }
          : ({ from: undefined, to: undefined } as DateRange),
      createdAt:
        createdAtFromValue || createdAtToValue
          ? {
              from: createdAtFromValue ? new Date(createdAtFromValue) : undefined,
              to: createdAtToValue ? new Date(createdAtToValue) : undefined,
            }
          : ({ from: undefined, to: undefined } as DateRange),
      label: query?.label || '',
      companyId: query?.companyId ? String(query?.companyId) : 'all',
      locationId: query?.locationId ? String(query?.locationId) : 'all',
      claim: query?.claim || 'none',
      mailbox: query?.mailbox || 'all',
    };
  };

  const getParams = (values: FormValues): Record<string, any> => {
    const params: Record<string, any> = {};

    params.name = values.name || undefined;
    params.orderType = values.orderType || undefined;
    params.statuses = values.statuses?.length ? values.statuses.join(',') : undefined;

    // Fix for deliveryDate - add proper null checks
    if (values.deliveryDate && typeof values.deliveryDate === 'object' && values.deliveryDate.from) {
      params.deliveryDateFrom = dayjs(values.deliveryDate.from).format(DATE_BACKEND_FORMAT);
    }
    if (values.deliveryDate && typeof values.deliveryDate === 'object' && values.deliveryDate.to) {
      params.deliveryDateTo = dayjs(values.deliveryDate.to).format(DATE_BACKEND_FORMAT);
    }

    // Fix for createdAt - add proper null checks
    if (values.createdAt && typeof values.createdAt === 'object' && values.createdAt.from) {
      params.createdAtFrom = dayjs(values.createdAt.from).format(DATE_BACKEND_FORMAT);
    }
    if (values.createdAt && typeof values.createdAt === 'object' && values.createdAt.to) {
      params.createdAtTo = dayjs(values.createdAt.to).format(DATE_BACKEND_FORMAT);
    }

    params.label = values.label || undefined;
    params.locationId = values.locationId !== 'all' ? values.locationId : undefined;
    params.companyId = values.companyId !== 'all' ? values.companyId : undefined;
    params.claimStatus = values.claim !== 'none' ? values.claim : undefined;
    params.mailbox = values.mailbox !== 'all' ? values.mailbox : undefined;

    return params;
  };

  const handleSubmit = (values: FormValues) => {
    const params = getParams(values);
    // Clean up params by removing undefined, null, and empty string values
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== undefined && value !== null && value !== '' && value !== 'all' && value !== 'none',
      ),
    );
    onSubmit(cleanParams, true);
    setShowDownload(true);
  };

  const download = async (values: FormValues) => {
    const params = { ...query, ...getParams(values) };

    // Remove undefined values
    Object.keys(params).forEach((k) => {
      if (params[k] === undefined) {
        delete params[k];
      }
    });

    params.take = 10000000; // Limited at 10000000

    const queryParams = new URLSearchParams(params).toString();

    const res = await fetch(`${process.env.REACT_APP_API_URI}/1/orders/csv-download?${queryParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${Cookies.get(TOKEN_NAME)}`,
      },
    });
    const data = await res.json();
    setTransactionData(data);
  };

  const handleResetButton = () => {
    onSubmit({}, true);
    form.reset();
    setShowDownload(false);
  };

  const changeSelectCompanyId = (value: string) => {
    if (value && value !== 'all') {
      setCompanySelectId(value);
      const newOptionsLocation = locations
        .filter((location: Location) => location.companyId === Number(value))
        .map((location: Location) => ({
          value: String(location.id),
          label: `${location.name} [${location.locationType}]`,
        }));
      const newOptionsLocationWithAll = [{ label: 'All Locations', value: 'all' }, ...newOptionsLocation];
      setOptionsLocation(newOptionsLocationWithAll);
    } else {
      setCompanySelectId('');
      setOptionsLocation([{ label: 'All Locations', value: 'all' }]);
      form.setValue('locationId', 'all');
    }
  };

  const mailboxOptions = [
    { label: 'ALL', value: 'all' },
    { label: 'Regular', value: 'false' },
    { label: 'Mailbox', value: 'true' },
  ];

  const claimOptions = [
    { value: 'none', label: '-' },
    { value: 'FILED', label: 'Filed' },
    { value: 'GRANTED', label: 'Granted' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'ALL', label: 'All' },
  ];

  const statusOptions = Object.keys(ORDER_STATUSES).map((key) => ({
    label: ORDER_STATUSES[key as keyof typeof ORDER_STATUSES],
    value: key,
  }));

  const companyOptions = [
    { label: 'All Companies', value: 'all' },
    ...sortBy(companies, 'name').map((company: Company) => ({
      value: String(company.id),
      label: company.name,
    })),
  ];

  return (
    <TooltipProvider>
      <div className="CommonFilter OrdersFilter w-full p-4 mt-4 rounded-lg border border-gray-200">
        {isFetching && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-50">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="FilterForm OrdersFilterForm space-y-4 w-full">
            <div className="FilterFormHeader flex items-center justify-between">
              <h4 className="text-lg font-semibold">Filter</h4>
              <div className="actions flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleResetButton}
                  className="flex items-center gap-1"
                >
                  Clear
                  <X className="h-4 w-4" />
                </Button>
                <Button type="submit" variant="link" size="sm" className="apply flex items-center gap-1">
                  Apply
                  <Check className="h-4 w-4" />
                </Button>
                {/* {showDownload && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={() => download(form.getValues())}
                        className="apply flex items-center gap-1"
                      >
                        Download CSV
                        <Download className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Download all orders that match the set filters</p>
                    </TooltipContent>
                  </Tooltip>
                )} */}
              </div>
            </div>

            <div className="FilterFormContent w-full">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 w-full">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Name" {...field} className="w-full" />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mailbox"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Mailbox</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select mailbox type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {mailboxOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="statuses"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Status</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-between">
                            {field.value?.length > 0 ? `${field.value.length} selected` : 'Select statuses'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full max-h-60 overflow-auto">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Select Statuses</span>
                            {field.value?.length > 0 && (
                              <button
                                type="button"
                                onClick={() => field.onChange([])}
                                className="text-xs text-muted-foreground hover:text-foreground"
                              >
                                Clear all
                              </button>
                            )}
                          </div>
                          {statusOptions.map((option) => {
                            const isSelected = field.value?.includes(option.value);
                            return (
                              <div
                                key={option.value}
                                className="flex items-center space-x-2 py-1"
                                onClick={() => {
                                  const current = field.value || [];
                                  if (isSelected) {
                                    field.onChange(current.filter((v) => v !== option.value));
                                  } else {
                                    field.onChange([...current, option.value]);
                                  }
                                }}
                              >
                                <Checkbox checked={isSelected} className="cursor-pointer" />

                                <Badge
                                  variant="outline"
                                  className={cn('text-sm whitespace-nowrap', getStatusBadgeStyles(option.label))}
                                >
                                  {formatOrderStatus(option.label)}
                                </Badge>
                              </div>
                            );
                          })}
                        </PopoverContent>
                      </Popover>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="deliveryDate"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Delivery Date</FormLabel>
                      <FormControl>
                        <DatePickerWithRange preset={field.value} onChange={field.onChange} width={280} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="companyId"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Company</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          changeSelectCompanyId(value);
                        }}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select company" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {companyOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="locationId"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Location</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value} disabled={!companySelectId}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="All locations" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {optionsLocation.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="claim"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Claim</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Claim status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {claimOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="createdAt"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Created Date</FormLabel>
                      <FormControl>
                        <DatePickerWithRange preset={field.value} onChange={field.onChange} width={280} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </form>
        </Form>
      </div>
    </TooltipProvider>
  );
};

export default OrdersFilter;
